/**
 * Local-install update surface (OFFLINE-DISTRIBUTION-PLAN.md §3.3/§3.7):
 * `GET /api/updates/status` feeds the update banner and Settings -> About;
 * `POST /api/updates/apply` / `check-now` need `settings.manage`. Ported from
 * ../lims/apps/web/src/lib/updates-api.ts.
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { LocalUpdateStatusDto, ShopOfflineAccessDto, UpdatesApplyBody } from '@pospak/shared';
import { api } from './api';
import { useAuth } from '../stores/auth';

export const UPDATE_STATUS_KEY = ['updates', 'status'] as const;
export const UPDATE_STATUS_POLL_MS = 10 * 60_000;
export const ABOUT_SETTINGS_PATH = '/settings?tab=about';

export function fetchUpdateStatus(): Promise<LocalUpdateStatusDto> {
  // No auth, no shop header — the same route the landing page reads over CORS.
  return api.get<LocalUpdateStatusDto>('/updates/status', { noShop: true });
}

export function useUpdateStatusQuery(options: { enabled?: boolean; refetchInterval?: number | false } = {}): UseQueryResult<LocalUpdateStatusDto> {
  return useQuery({
    queryKey: UPDATE_STATUS_KEY,
    queryFn: fetchUpdateStatus,
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval ?? UPDATE_STATUS_POLL_MS,
    staleTime: 60_000,
    retry: false,
  });
}

export function useCheckForUpdatesMutation(): UseMutationResult<LocalUpdateStatusDto, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<LocalUpdateStatusDto>('/updates/check-now', undefined, { noShop: true }),
    onSuccess: (data) => queryClient.setQueryData(UPDATE_STATUS_KEY, data),
  });
}

export function useApplyUpdateMutation(): UseMutationResult<{ started: boolean }, unknown, UpdatesApplyBody> {
  return useMutation({
    mutationFn: (body: UpdatesApplyBody) => api.post<{ started: boolean }>('/updates/apply', body, { noShop: true }),
  });
}

/** `POST /api/shops/:id/download-pin` for the ACTIVE shop — the PIN is per
 * shop because one local install serves one shop. */
export function useRegenerateDownloadPinMutation(): UseMutationResult<{ pin: string }, unknown, void> {
  return useMutation({
    mutationFn: () => {
      const shopId = useAuth.getState().activeShopId;
      if (!shopId) return Promise.reject(new Error('Pick a shop first.'));
      return api.post<{ pin: string }>(`/shops/${encodeURIComponent(shopId)}/download-pin`);
    },
  });
}

export const OFFLINE_ACCESS_KEY = ['shops', 'offline-access'] as const;

/** `GET /api/shops/:id/offline-access` for the ACTIVE shop — whether the
 * owner has turned on "Allow offline mode" (hides the download-PIN card). */
export function useOfflineAccessQuery(shopId: string | null): UseQueryResult<ShopOfflineAccessDto> {
  return useQuery({
    queryKey: [...OFFLINE_ACCESS_KEY, shopId],
    queryFn: () => api.get<ShopOfflineAccessDto>(`/shops/${encodeURIComponent(shopId ?? '')}/offline-access`),
    enabled: shopId !== null,
    staleTime: 60_000,
    retry: false,
  });
}
